/**
 * React Hooks for Materials Versioning
 * 
 * Provides React hooks for version management operations,
 * following existing patterns from the codebase.
 */

import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import {
  MaterialsVersionControl,
  VersionInheritanceManager,
  RollbackImpactAnalyzer,
  VersionComparator,
  VersionUsageAnalytics
} from '../lib/materials-versioning.js';
import { 
  fetchVersionsForSession,
  getLatestMasterVersion,
  getVersionFiles,
  logMaterialAccess
} from '../lib/materials-airtable.js';

/**
 * Hook for managing versions for a specific session
 */
export const useSessionVersions = (sessionId) => {
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadVersions = useCallback(async () => {
    if (!sessionId) return;

    setLoading(true);
    setError(null);

    try {
      const [versionsData, masterVersion] = await Promise.all([
        fetchVersionsForSession(sessionId),
        getLatestMasterVersion(sessionId)
      ]);

      setVersions(versionsData);
      setCurrentVersion(masterVersion);
    } catch (err) {
      setError(err.message);
      Sentry.captureException(err, {
        tags: { hook: 'useSessionVersions' },
        extra: { sessionId }
      });
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  const refreshVersions = useCallback(() => {
    loadVersions();
  }, [loadVersions]);

  return {
    versions,
    currentVersion,
    loading,
    error,
    refreshVersions
  };
};

/**
 * Hook for creating new versions
 */
export const useVersionCreation = () => {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const createVersion = useCallback(async (sessionId, newFiles, changeNotes, adminUser) => {
    setCreating(true);
    setError(null);

    try {
      const result = await MaterialsVersionControl.createNewVersion(
        sessionId, 
        newFiles, 
        changeNotes, 
        adminUser
      );

      return result;
    } catch (err) {
      setError(err.message);
      Sentry.captureException(err, {
        tags: { hook: 'useVersionCreation' },
        extra: { sessionId, filesCount: newFiles?.length, adminUser }
      });
      throw err;
    } finally {
      setCreating(false);
    }
  }, []);

  return {
    createVersion,
    creating,
    error
  };
};

/**
 * Hook for version inheritance management
 */
export const useVersionInheritance = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const determineSOVersion = useCallback(async (sessionId, soId, isNewSO = true) => {
    setLoading(true);
    setError(null);

    try {
      const version = await VersionInheritanceManager.determineVersionForSO(
        sessionId,
        soId,
        isNewSO
      );

      return version;
    } catch (err) {
      setError(err.message);
      Sentry.captureException(err, {
        tags: { hook: 'useVersionInheritance' },
        extra: { sessionId, soId, isNewSO }
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const lockVersionForSO = useCallback(async (soId, versionId, sessionId) => {
    setLoading(true);
    setError(null);

    try {
      const result = await VersionInheritanceManager.lockVersionForSO(
        soId,
        versionId,
        sessionId
      );

      return result;
    } catch (err) {
      setError(err.message);
      Sentry.captureException(err, {
        tags: { hook: 'useVersionInheritance' },
        extra: { soId, versionId, sessionId }
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    determineSOVersion,
    lockVersionForSO,
    loading,
    error
  };
};

/**
 * Hook for rollback analysis and execution
 */
export const useVersionRollback = () => {
  const [analyzing, setAnalyzing] = useState(false);
  const [rolling, setRolling] = useState(false);
  const [error, setError] = useState(null);

  const analyzeRollbackImpact = useCallback(async (sessionId, targetVersionId) => {
    setAnalyzing(true);
    setError(null);

    try {
      const impact = await RollbackImpactAnalyzer.analyzeRollbackImpact(
        sessionId,
        targetVersionId
      );

      return impact;
    } catch (err) {
      setError(err.message);
      Sentry.captureException(err, {
        tags: { hook: 'useVersionRollback', operation: 'analyze' },
        extra: { sessionId, targetVersionId }
      });
      throw err;
    } finally {
      setAnalyzing(false);
    }
  }, []);

  const executeRollback = useCallback(async (sessionId, targetVersionId, adminUser) => {
    setRolling(true);
    setError(null);

    try {
      const result = await MaterialsVersionControl.rollbackToVersion(
        sessionId,
        targetVersionId,
        adminUser
      );

      return result;
    } catch (err) {
      setError(err.message);
      Sentry.captureException(err, {
        tags: { hook: 'useVersionRollback', operation: 'execute' },
        extra: { sessionId, targetVersionId, adminUser }
      });
      throw err;
    } finally {
      setRolling(false);
    }
  }, []);

  return {
    analyzeRollbackImpact,
    executeRollback,
    analyzing,
    rolling,
    error
  };
};

/**
 * Hook for comparing versions
 */
export const useVersionComparison = () => {
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState(null);

  const compareVersions = useCallback(async (versionId1, versionId2) => {
    setComparing(true);
    setError(null);

    try {
      const comparison = await VersionComparator.compareVersions(versionId1, versionId2);
      return comparison;
    } catch (err) {
      setError(err.message);
      Sentry.captureException(err, {
        tags: { hook: 'useVersionComparison' },
        extra: { versionId1, versionId2 }
      });
      throw err;
    } finally {
      setComparing(false);
    }
  }, []);

  return {
    compareVersions,
    comparing,
    error
  };
};

/**
 * Hook for version usage analytics
 */
export const useVersionAnalytics = (sessionId, timeRange = '30days') => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadAnalytics = useCallback(async () => {
    if (!sessionId) return;

    setLoading(true);
    setError(null);

    try {
      const stats = await VersionUsageAnalytics.getVersionUsageStats(sessionId, timeRange);
      setAnalytics(stats);
    } catch (err) {
      setError(err.message);
      Sentry.captureException(err, {
        tags: { hook: 'useVersionAnalytics' },
        extra: { sessionId, timeRange }
      });
    } finally {
      setLoading(false);
    }
  }, [sessionId, timeRange]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const refreshAnalytics = useCallback(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  return {
    analytics,
    loading,
    error,
    refreshAnalytics
  };
};

/**
 * Hook for managing version files
 */
export const useVersionFiles = (versionId) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadFiles = useCallback(async () => {
    if (!versionId) return;

    setLoading(true);
    setError(null);

    try {
      const filesData = await getVersionFiles(versionId);
      setFiles(filesData);
    } catch (err) {
      setError(err.message);
      Sentry.captureException(err, {
        tags: { hook: 'useVersionFiles' },
        extra: { versionId }
      });
    } finally {
      setLoading(false);
    }
  }, [versionId]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const refreshFiles = useCallback(() => {
    loadFiles();
  }, [loadFiles]);

  return {
    files,
    loading,
    error,
    refreshFiles
  };
};

/**
 * Hook for tracking material access
 */
export const useMaterialAccess = () => {
  const [logging, setLogging] = useState(false);

  const logAccess = useCallback(async (fileId, accessMethod, soContext = null) => {
    setLogging(true);

    try {
      // Get basic client info for tracking (without PII)
      const userIpHash = null; // Will be handled by server-side logging
      const countryCode = null; // Will be handled by CDN/server-side

      await logMaterialAccess(fileId, accessMethod, soContext, userIpHash, countryCode);
    } catch (err) {
      // Don't throw errors for access logging - it shouldn't break user experience
      Sentry.captureException(err, {
        tags: { hook: 'useMaterialAccess' },
        extra: { fileId, accessMethod, soContext }
      });
    } finally {
      setLogging(false);
    }
  }, []);

  return {
    logAccess,
    logging
  };
};

/**
 * Combined hook for comprehensive version management
 */
export const useMaterialsVersionControl = (sessionId) => {
  const { versions, currentVersion, loading: versionsLoading, refreshVersions } = useSessionVersions(sessionId);
  const { createVersion, creating } = useVersionCreation();
  const { determineSOVersion, lockVersionForSO } = useVersionInheritance();
  const { analyzeRollbackImpact, executeRollback } = useVersionRollback();
  const { compareVersions } = useVersionComparison();
  const { analytics, refreshAnalytics } = useVersionAnalytics(sessionId);
  const { logAccess } = useMaterialAccess();

  // Combined loading state
  const loading = versionsLoading || creating;

  // Comprehensive refresh function
  const refresh = useCallback(() => {
    refreshVersions();
    refreshAnalytics();
  }, [refreshVersions, refreshAnalytics]);

  return {
    // Version data
    versions,
    currentVersion,
    analytics,
    
    // Loading states
    loading,
    
    // Operations
    createVersion,
    determineSOVersion,
    lockVersionForSO,
    analyzeRollbackImpact,
    executeRollback,
    compareVersions,
    logAccess,
    
    // Refresh functions
    refresh,
    refreshVersions,
    refreshAnalytics
  };
};